import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK } from "../../../theme/tokens";

type Props = {
	// Comma-separated extensions (e.g. ".csv,.xls,.xlsx"). Also used to filter files dropped via drag-and-drop.
	accept: string;
	// Text shown inside the drop zone.
	hint: string;
	multiple?: boolean;
	onFiles: (files: File[]) => void;
	/**
	 * Files whose extension is not in `accept`. Without this the zone drops them silently and the
	 * picker's own filter does not apply to a drag-and-drop, so the user is left believing a file was
	 * accepted. Optional — a caller that does not care keeps the old silent behaviour.
	 */
	onRejected?: (files: File[]) => void;
	/** Optional glyph above the hint (e.g. an upload arrow) — turns the zone into a roomier, titled target. */
	icon?: ReactNode;
	/** Optional muted line under the hint inside the zone (accepted types, size limit). */
	caption?: string;
	/**
	 * `stacked` (default): centred, glyph above the text — the main target of its surface (an upload dialog).
	 * `row`: one slim start-aligned row, glyph beside the text and no bottom margin — for a zone that shares its
	 * page with other content and must not dominate it (an empty slot waiting for one file).
	 */
	layout?: "stacked" | "row";
	/** Merged last — the caller places the zone (a margin, a width). */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

const openPickerKeys = new Set(["Enter", " "]);

/**
 * Whether `file`'s name ends in one of `accept`'s comma-separated extensions (empty `accept` = anything).
 * Exported so every "accept these file types" control (this drop zone, an inline attach button) filters
 * a picked or dropped file by exactly the same rule.
 */
export const isFileAccepted = (file: File, accept: string): boolean => {
	const exts = accept
		.split(",")
		.map((e) => e.trim().toLowerCase())
		.filter(Boolean);
	if (exts.length === 0) return true;
	const name = file.name.toLowerCase();
	return exts.some((ext) => name.endsWith(ext));
};

export function FileDropZone({ accept, hint, multiple = false, onFiles, onRejected, icon, caption, layout = "stacked", sx, dataTestId }: Props) {
	const inputRef = useRef<HTMLInputElement>(null);
	// A file held over the zone lights it up like a hover does — a drag fires no hover in most browsers.
	const [dragOver, setDragOver] = useState(false);
	const titled = icon !== undefined || caption !== undefined;
	const row = layout === "row";

	const handleFiles = useCallback(
		(fileList: FileList | null) => {
			if (!fileList) return;
			const all = Array.from(fileList);
			const filtered = all.filter((f) => isFileAccepted(f, accept));
			const rejected = all.filter((f) => !isFileAccepted(f, accept));
			if (filtered.length > 0) onFiles(filtered);
			if (rejected.length > 0) onRejected?.(rejected);
		},
		[accept, onFiles, onRejected],
	);

	return (
		<Box
			data-testid={dataTestId}
			// A real control for keyboard users too: Tab reaches it, Enter / Space opens the picker.
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (!openPickerKeys.has(e.key)) return;
				e.preventDefault();
				inputRef.current?.click();
			}}
			sx={[
				(theme) => ({
					border: `2px dashed ${theme.palette.primary.main}`,
					borderRadius: 2,
					p: 3,
					textAlign: "center",
					cursor: "pointer",
					mb: 2,
					...(dragOver ? { bgcolor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) } : {}),
					"&:hover": { bgcolor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) },
					"&:focus-visible": { outline: `2px solid ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`, outlineOffset: 2 },
				}),
				// The titled variant (icon / caption) is the primary target of its surface, so it gets more room.
				titled && !row ? { py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 } : {},
				row ? { display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, textAlign: "start", mb: 0 } : {},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			onClick={() => inputRef.current?.click()}
			onDragOver={(e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!dragOver) setDragOver(true);
			}}
			onDragLeave={(e) => {
				// Moving onto a child also "leaves" the zone; only a pointer that left the zone itself counts.
				const next = e.relatedTarget;
				if (next instanceof Node && e.currentTarget.contains(next)) return;
				setDragOver(false);
			}}
			onDrop={(e) => {
				e.preventDefault();
				e.stopPropagation();
				setDragOver(false);
				handleFiles(e.dataTransfer.files);
			}}
		>
			{icon !== undefined && (
				<Box aria-hidden sx={{ display: "flex", flexShrink: 0, color: "primary.main", "& svg": { fontSize: ICON_SIZE.FEATURE } }}>
					{icon}
				</Box>
			)}
			<Box sx={row ? { minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 } : { display: "contents" }}>
				<Typography variant="subtitle1" sx={titled ? { fontWeight: 700 } : undefined}>
					{hint}
				</Typography>
				{caption !== undefined && <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{caption}</Typography>}
			</Box>
			<input
				ref={inputRef}
				hidden
				type="file"
				multiple={multiple}
				accept={accept}
				onChange={(e) => {
					handleFiles(e.target.files);
					if (inputRef.current) inputRef.current.value = "";
				}}
			/>
		</Box>
	);
}
